import { createFileRoute, Link } from '@tanstack/react-router'
import { Building2, Pencil } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import * as m from '~/paraglide/messages'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

export const Route = createFileRoute('/dashboard/business')({
  component: BusinessProfile,
})

function BusinessProfile() {
  const { data: business, isLoading } = useCurrentBusiness()

  if (isLoading) {
    return <BusinessProfileSkeleton />
  }

  if (!business) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <Building2 className="mb-4 w-16 h-16 text-muted-foreground" />
        <h2 className="mb-2 font-semibold text-2xl">{m.noBusinessProfileFound()}</h2>
        <p className="text-muted-foreground">{m.pleaseCreateABusinessProfileToGetStarted()}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={m.businessProfile()}
        description={m.viewYourBusinessAnalyticsAndInsights()}
        action={
          <Button asChild className="w-full sm:w-auto">
            <Link to="/dashboard/business/edit">
              <Pencil className="h-4 w-4" />
              {m.editProfile()}
            </Link>
          </Button>
        }
      />
      <ComingSoonCard
        title={m.businessAnalyticsInsights()}
        description={m.trackYourBusinessPerformanceCustomerReviewsAndEngagementMetrics()}
      />
    </div>
  )
}

function ComingSoonCard({ title, description }: { title: string; description: string }) {
  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-center items-center py-8">
          <Badge variant="secondary" className="text-xs">
            {m.comingSoon()}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}

function BusinessProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <Skeleton className="mb-2 w-64 max-w-full h-8" />
          <Skeleton className="w-96 max-w-full h-4" />
        </div>
        <Skeleton className="w-32 h-10" />
      </div>

      {Array.from({ length: 3 }, (_, i) => `business-skeleton-${i}`).map((key) => (
        <Card key={key}>
          <CardHeader>
            <Skeleton className="mb-2 w-48 h-6" />
            <Skeleton className="w-64 h-4" />
          </CardHeader>
          <CardContent>
            <Skeleton className="w-full h-32" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
